/**
 * Doubt, propagated mechanically.
 *
 * A refutation lands on one node. Everything reasoned beneath it, before it
 * arrived, was derived from something the record has since contradicted — and
 * until now the graph said so one hop deep and no further: `staleParent` in the
 * renderer detaches a child from a parent that has since been measured, and
 * stops there. A grandchild of a refuted claim rendered as though nothing had
 * happened, at full confidence, and an autonomous run would keep growing it.
 *
 * This walks the rest of that distance. It is reachability plus one timestamp
 * comparison — when the node's words were written (`writtenAt`: a rewrite's
 * `editedAt`, else `createdAt`) against when the refutation landed, `recordedAt`
 * rather than `at` for the reason `Outcome` documents — so a reader holding the
 * graph can re-derive every label here by hand. Nothing is stored,
 * nothing is asked of a model, and no number is invented: a tainted node keeps
 * its own confidence, because the model's rating of its own work is not evidence
 * about it and quietly discounting it here would fabricate a measurement.
 *
 * What taint asserts is narrow and worth stating exactly: *this was reasoned
 * from something that has since been refuted, and nobody has re-derived it.*
 * Not that it is wrong. A conclusion can survive the collapse of the argument
 * that reached it, which is why nothing here touches a channel, a hue, or a
 * score — it marks a subtree as owed a re-derivation and leaves the judgement to
 * the person whose graph it is.
 */

import { outcomeLive, verificationLive } from './types'
import type { ReasonNode, Session } from './types'
import { dependencyChildren } from './rebase'
import { writtenAt } from './rewrite'
import { calendarDay } from './dates'
import { STATUS_WORD } from './vocabulary'

/** The refutation a node inherited, and where it came from. */
export interface Taint {
  /** The nearest ancestor whose claim was refuted. */
  sourceId: string
  /**
   * When that refutation landed — `recordedAt` for an outcome, `checkedAt` for a
   * check. Never `Outcome.at`: results are written up late, and the observation's
   * own date would mark reasoning as predating a result its author could not
   * possibly have known.
   */
  at: number
  /**
   * How it arrived. A recorded result and a source check are not the same
   * authority, and a reader deciding whether to re-derive should not have to
   * open the node to find out which one broke it.
   */
  via: 'outcome' | 'verification'
}

/**
 * The refutation a node carries in its own right, if any.
 *
 * Precedence follows `liveChannelOf` exactly, and for its reasons: an
 * observation outranks a source, because a source only *says*. A check is
 * admitted only while `verificationLive` holds — a node edited after its check
 * was checked as different words, and propagating doubt from it would spread a
 * reading of text nobody ever searched for.
 */
function ownRefutation(n: ReasonNode): Taint | null {
  // `outcomeLive` beside the `verificationLive` below, and this is the site the
  // header's argument was already about: propagating doubt from a record of
  // different words spreads a reading nobody made of this claim. It is the
  // sharper of the two, because a refutation SOURCED here is not local —
  // invariant 2 names this file as what carries one down the whole subtree.
  if (n.outcome?.bearing === 'refuted' && outcomeLive(n)) {
    return { sourceId: n.id, at: n.outcome.recordedAt, via: 'outcome' }
  }
  if (n.verification?.reading === 'refuted' && verificationLive(n)) {
    return { sourceId: n.id, at: n.verification.checkedAt, via: 'verification' }
  }
  return null
}

/** One walk, four readings of it, memoised together on the node map. */
interface Walked {
  /** Every node standing on a refutation — `taintMap`. */
  map: ReadonlyMap<string, Taint>
  /** The same map read the other way: each refuted claim, and the nodes
   *  standing on it in arrival order — `standingOn`. */
  /** EVERY refutation that reaches a node, not only the nearest one `map`
   *  retains — `taintSources`. */
  sources: ReadonlyMap<string, ReadonlySet<string>>
  bySource: ReadonlyMap<string, readonly string[]>
  /** Nodes the walk would have reached but for a rewrite after the refutation
   *  landed — `rewrittenOver`. */
  rewritten: ReadonlyMap<string, Taint>
}

let cache: { nodes: Record<string, ReasonNode>; walked: Walked } | null = null

/**
 * Every node standing on a refutation, mapped to the one it stands on.
 *
 * Two rules decide the walk, and both exist to keep this from overstating:
 *
 * **A node carrying a recorded outcome is exempt, and stops the descent.** Its
 * claim was not reasoned to, it was observed, and an observation does not become
 * doubtful because the argument that suggested looking there fell over. This is
 * the same exemption the display filters already make for a node with a result,
 * and the same authority ordering `channelOf` encodes. A check does NOT earn
 * this — only the record the tool says outranks the model.
 *
 * **The nearest refuted ancestor wins.** That is the join the refutation
 * actually invalidated, which is the precedent the renderer set when it chose to
 * detach a stale strand at its parent rather than at the root.
 *
 * Memoised on the identity of `nodes`, which the store replaces on every change:
 * the renderer asks per frame, and an O(n) walk per frame during a settle would
 * be paid on top of the geometry rebuild that is already there.
 */
export function taintMap(s: Session): ReadonlyMap<string, Taint> {
  return walk(s).map
}

/**
 * EVERY refutation that reaches a node, not only the nearest one `taintMap`
 * retains.
 *
 * The walk below already runs once per refutation — it has to, or whichever
 * parent of a synthesis was visited first would erase the taint arriving
 * through the other. It then keeps one candidate per node, because the strand
 * the canvas detaches and the section the report prints each want a single
 * nearest cause.
 *
 * `standing.ts` wants the other thing. A synthesis fusing two independently
 * refuted branches is undercut by both, and its WHY sentence promises to name
 * both — it cannot, reading a map that holds one. Every source that reached a
 * node under THESE rules is collected here rather than recomputed there, so the
 * two can no more disagree about which refutations reach a node than they can
 * about the nearest.
 */
export function taintSources(s: Session): ReadonlyMap<string, ReadonlySet<string>> {
  return walk(s).sources
}

function walk(s: Session): Walked {
  if (cache && cache.nodes === s.nodes) return cache.walked

  const out = new Map<string, Taint>()
  const bySource = new Map<string, string[]>()
  const reached = new Map<string, Set<string>>()
  const rewritten = new Map<string, Taint>()
  const rootId = s.order.find((id) => s.nodes[id]?.parentId === null)
  if (!rootId) {
    const walked = { map: out, bySource, sources: reached, rewritten }
    cache = { nodes: s.nodes, walked }
    return walked
  }

  const dependents = dependencyChildren(s)
  // Keep the old fail-closed boundary for malformed imports: a component that
  // is not reachable from the board's root cannot acquire a graph-wide label.
  const reachable = new Set<string>()
  const reachStack = [rootId]
  while (reachStack.length) {
    const id = reachStack.pop()!
    if (reachable.has(id)) continue
    reachable.add(id)
    for (const child of dependents.get(id) ?? []) reachStack.push(child)
  }

  interface Candidate {
    taint: Taint
    distance: number
  }
  const candidates = new Map<string, Candidate>()
  const spared = new Map<string, Candidate>()
  const outranks = (next: Candidate, held: Candidate | undefined): boolean =>
    !held ||
    next.distance < held.distance ||
    (next.distance === held.distance &&
      (next.taint.at > held.taint.at ||
        (next.taint.at === held.taint.at &&
          next.taint.via === 'outcome' &&
          held.taint.via !== 'outcome') ||
        (next.taint.at === held.taint.at &&
          next.taint.via === held.taint.via &&
          next.taint.sourceId < held.taint.sourceId)))

  // A synthesis has more than one incoming dependency. Walking once from the
  // root would let whichever parent happened to be visited first erase taint
  // arriving through the other parent. Walk once from each refutation instead,
  // then retain the nearest deterministic candidate at every dependent node.
  for (const sourceId of s.order) {
    if (!reachable.has(sourceId)) continue
    const source = s.nodes[sourceId]
    if (!source) continue
    const taint = ownRefutation(source)
    if (!taint) continue

    const queue = (dependents.get(sourceId) ?? []).map((id) => ({ id, distance: 1 }))
    const shortest = new Map<string, number>([[sourceId, 0]])
    for (let at = 0; at < queue.length; at++) {
      const { id, distance } = queue[at]!
      const seenAt = shortest.get(id)
      if (seenAt !== undefined && seenAt <= distance) continue
      shortest.set(id, distance)
      const node = s.nodes[id]
      if (!node || !reachable.has(id)) continue

      // Reasoned after the refutation is reasoned in full knowledge of it —
      // which is exactly what re-deriving produces. Its older descendants still
      // need review, so the walk continues through that untainted node.
      //
      // "Reasoned" is when the words now on the node were written, which for a
      // claim the reader rewrote is the rewrite (`writtenAt`): the rewrite's
      // press IS "Re-derive from here", its words were typed with the card
      // saying what they stood on, and the subtree below them is re-derived by
      // the same press. The coach's `world` count already read it this way; the
      // walk read `createdAt`, and so went on telling the reader that her own
      // sentence was "reasoned before" a refutation she had written it after.
      // The claim above is still refuted, and the card still names it
      // (`rewrittenOver`) — what the rewrite ends is the owed re-derivation.
      //
      // `outcomeLive` reads false for a node with no outcome at all, so this is
      // the same test it always was plus the stale case: a record about wording
      // this claim no longer carries must not decide whether the claim counts as
      // re-derived.
      if (!outcomeLive(node)) {
        const next = { taint, distance }
        if (writtenAt(node) < taint.at) {
          if (outranks(next, candidates.get(id))) candidates.set(id, next)
          // Every source that got here, beside the one that wins. Same rules, same
          // walk — the set and the nearest cannot describe different graphs.
          const all = reached.get(id)
          if (all) all.add(sourceId)
          else reached.set(id, new Set([sourceId]))
        } else if (node.createdAt < taint.at) {
          if (outranks(next, spared.get(id))) spared.set(id, next)
        }
      }
      // A recorded result is an authority boundary for every incoming path. A
      // refuting result begins its own walk when its source turn is visited.
      // LIVE, for the reason the two above are: the exemption is the world's,
      // and a record naming a proposition this claim no longer makes is not this
      // claim's to be exempted by.
      if (outcomeLive(node)) continue
      for (const child of dependents.get(id) ?? [])
        queue.push({ id: child, distance: distance + 1 })
    }
  }

  // In arrival order, so `bySource` lists what stands on a claim in the order a
  // reader meets it and every consumer of the map iterates the same way.
  for (const id of s.order) {
    const candidate = candidates.get(id)
    if (candidate) {
      out.set(id, candidate.taint)
      const list = bySource.get(candidate.taint.sourceId)
      if (list) list.push(id)
      else bySource.set(candidate.taint.sourceId, [id])
    }
    // Spared by a rewrite only where nothing else still reaches it: a claim
    // rewritten after one refutation can stand on an older-written claim below
    // a second, and that one it does stand on.
    const kept = spared.get(id)
    if (kept && !candidate) rewritten.set(id, kept.taint)
  }

  const walked: Walked = { map: out, bySource, sources: reached, rewritten }
  cache = { nodes: s.nodes, walked }
  return walked
}

/**
 * The nodes standing on one refuted claim — every node whose card names it as
 * what it stands on, the report's list for it, the manifest's rows for it — in
 * arrival order. Down every strand, a synthesis's second parent included, and
 * nearest-wins: a node below a second, nearer refutation stands on that one and
 * is counted there. Empty for a claim nothing stands on.
 *
 * The coach's `world` cue counts this. It walked the tree by parent, so where a
 * synthesis was joined from the refuted branch it counted one fewer than the
 * card and the report — and it walked through a recorded result, which the walk
 * above treats as an authority boundary.
 */
export function standingOn(s: Session, sourceId: string): readonly string[] {
  return walk(s).bySource.get(sourceId) ?? []
}

/**
 * The refutation a node would stand on had the reader not rewritten it after
 * that refutation landed — or null. Nothing is owed a re-derivation there (see
 * the walk), but the claim above it is still refuted, and the card is where a
 * reader deciding what to do with her own sentence reads that: it names the
 * refuted claim, one press away, and says her words came after it
 * (`rewrittenOverSentence`).
 */
export function rewrittenOver(s: Session, nodeId: string): RefutedUnder | null {
  const taint = walk(s).rewritten.get(nodeId)
  return taint ? describe(s, nodeId, taint) : null
}

/** Whether this one node stands on a refutation. */
export function taintOf(s: Session, nodeId: string): Taint | null {
  return taintMap(s).get(nodeId) ?? null
}

/**
 * A node's taint as a surface names it: the refuted claim itself, and — for a
 * synthesis — the other branch it arrives through.
 *
 * `Taint` carries only an id, and every surface that printed one had to look the
 * claim up and then could not say how the doubt got there. For a claim in the
 * refuted claim's own branch the path is the one the thread already walks; a
 * synthesis joins two branches, and one standing on a refutation through the
 * branch it does NOT hang under read, in the report, as "stands on 1 …" from
 * inside branch 2 with nothing to say why. `through` names the joined claim the
 * refutation arrives by, and is absent whenever the node's own parent carries it
 * — the walk's nearest source reaches it along the path the reader already sees.
 *
 * Null where there is no taint, and where the source has since left the graph,
 * which cannot happen through the walk (it reads only nodes on the board) but can
 * through a caller holding a stale map.
 */
export interface RefutedUnder {
  source: ReasonNode
  via: Taint['via']
  at: number
  through?: ReasonNode
}

export function refutedUnder(s: Session, nodeId: string): RefutedUnder | null {
  const taint = taintOf(s, nodeId)
  return taint ? describe(s, nodeId, taint) : null
}

/** A walked taint as a surface names it: the refuted claim, and the joined claim
 *  it arrives through where the node's own parent does not carry it. */
function describe(s: Session, nodeId: string, taint: Taint): RefutedUnder | null {
  const node = s.nodes[nodeId]
  const source = s.nodes[taint.sourceId]
  if (!node || !source) return null
  // A parent carries it when the parent is the refuted claim, stands on it, or
  // would have but for its own rewrite — the path the reader already sees.
  const carries = (id: string | null | undefined): boolean =>
    !!id &&
    (id === source.id ||
      taintOf(s, id)?.sourceId === source.id ||
      walk(s).rewritten.get(id)?.sourceId === source.id)
  const out: RefutedUnder = { source, via: taint.via, at: taint.at }
  if (carries(node.parentId)) return out
  const through = (node.bridgeFrom ?? []).find((id) => id !== node.parentId && carries(id))
  const joined = through ? s.nodes[through] : undefined
  return joined && joined.id !== source.id ? { ...out, through: joined } : out
}

/**
 * What a node standing on a refutation stands on, in one sentence: what refuted
 * it, on which day, and — for a synthesis — which claim it reaches it through.
 * The card's line, its provenance fold and both Markdown documents print this
 * one sentence, so the screen and the file cannot say it two ways.
 *
 * "Not re-derived since" is exactly what the walk asserts (see the header): the
 * node predates the refutation, and nothing written after it replaced it. It
 * says nothing about whether the claim is wrong, and the surfaces add that it
 * does not.
 */
export function refutedUnderSentence(r: RefutedUnder): string {
  return `Reasoned before ${refutationClause(r)}, and not re-derived since.${throughClause(r)}`
}

/**
 * The card's sentence for `rewrittenOver`: the refutation, when, that the words
 * came after it, and that the claim above is still refuted — which is what the
 * rewrite did NOT change, and the one thing the card must go on saying.
 */
export function rewrittenOverSentence(r: RefutedUnder, editedAt: number): string {
  return `${STATUS_WORD.revisedByYou} on ${calendarDay(editedAt)}, after ${refutationClause(r)}. Your wording came after that record, so nothing here is owed a re-derivation — but “${r.source.label}” is still refuted.${throughClause(r)}`
}

/** What refuted the claim, and on which of the reader's days (D210). */
function refutationClause(r: RefutedUnder): string {
  const day = calendarDay(r.at)
  return r.via === 'outcome'
    ? `a result recorded on ${day} went against “${r.source.label}”`
    : `a source check on ${day} read “${r.source.label}” as stating the opposite`
}

const throughClause = (r: RefutedUnder): string =>
  r.through
    ? ` It reaches that claim through “${r.through.label}”, one of the claims it joins.`
    : ''
